import Axios from 'api/client';
import {
    Campaign,
    CampaignPayoutOption,
    Payout,
    PayoutAccount,
    PayoutFilter,
    PayoutType,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface InitiateTransferParams {
    campaignId: number;
    tip: number;
    amount: number;
    payout: number;
    type: PayoutType;
    fee: number;
    default_payout_tip?: number;
    instant_eligible: boolean;
}

interface PayoutsParams extends FilterParams {}

interface CampaignPayoutsParams extends FilterParams {
    filter?: PayoutFilter;
}

interface CampaignPayoutOptionsResponse {
    amounts: Record<string, CampaignPayoutOption>;
}

const fetchPayoutAccount = (accountId: number) =>
    Axios.get<PayoutAccount>(`/accounts/${accountId}/payout_account`)
        .then(({ data }) => data)
        .catch(() => ({ has_bank_account: false }) as PayoutAccount);

const fetchPayouts = (accountId: number, params: PayoutsParams = {}) => {
    const { page = 1, scope = 'all' } = params;

    return Axios.get<PaginatedData<Payout>>(`/accounts/${accountId}/payouts`, {
        params: { page, scope },
    }).then(({ data }) => data);
};

const fetchCampaignPayouts = (accountId: number, params: CampaignPayoutsParams = {}) => {
    const { page = 1, scope = 'all', ...rest } = params;

    return Axios.get<PaginatedData<Campaign>>(`/ajax/accounts/${accountId}/campaign_payouts`, {
        params: {
            page,
            scope,
            ...rest,
        },
    }).then(({ data }) => data);
};

const fetchCampaignPayoutOptions = (
    accountId: number,
    campaignId: number,
    params: { available_tip_percentages: number[] },
) => {
    return Axios.get<CampaignPayoutOptionsResponse>(
        `/ajax/accounts/${accountId}/campaigns/${campaignId}/payout_options`,
        {
            params,
        },
    ).then(({ data }) => data);
};

const exportIndividualPayout = (accountId: number, payoutId: number) =>
    Axios.post(`/accounts/${accountId}/exports`, {
        object: 'payout',
        report_type: 'csv',
        object_id: payoutId.toString(),
        resource: 'payout_ledgers',
    });

const exportPayouts = (accountId: number) =>
    Axios.post(`/accounts/${accountId}/exports`, {
        object: 'account',
        report_type: 'csv',
        resource: 'payouts',
        object_id: accountId.toString(),
    });

const initiateTransfer = (
    accountId: number,
    {
        campaignId: campaign_id,
        tip,
        amount,
        payout,
        type,
        fee,
        default_payout_tip,
        instant_eligible,
    }: InitiateTransferParams,
) =>
    Axios.post(`/accounts/${accountId}/payouts`, {
        method: 'bank',
        campaign_id,
        tip,
        amount,
        payout,
        type,
        fee,
        default_payout_tip,
        instant_eligible,
    });

export {
    fetchPayoutAccount,
    fetchPayouts,
    fetchCampaignPayouts,
    fetchCampaignPayoutOptions,
    exportIndividualPayout,
    exportPayouts,
    initiateTransfer,
};
export type { CampaignPayoutsParams, InitiateTransferParams, PayoutsParams };
